import React, { useState, useEffect } from 'react';
import { getUserProfile, updateUserProfile, getSupportedCurrencies, getConnectedAccounts, linkEmail, mergeAccounts as mergeAccountsApi, deleteData as deleteDataApi, deleteAccount as deleteAccountApi } from '../services/settingsApi';
import { getExportSchedule, saveExportSchedule, exportNow } from '../services/exportScheduleApi';
import { useAuth } from '../context/AuthContext';
import MergeConfirmModal from '../components/MergeConfirmModal';
import supabase from '../supabaseClient';

function SettingsPage() {
  const { isTelegramOnlyUser, signInWithOAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [, setProfile] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Connected accounts state
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [linkEmailInput, setLinkEmailInput] = useState('');
  const [linkingEmail, setLinkingEmail] = useState(false);
  const [accountsError, setAccountsError] = useState(null);
  const [accountsSuccess, setAccountsSuccess] = useState(null);
  const [mergeState, setMergeState] = useState(null);

  // Delete data state
  const [deleteDataCategories, setDeleteDataCategories] = useState({
    receipts: false,
    expenses: false,
    email_forwarding: false,
    telegram: false,
  });
  const [deletingData, setDeletingData] = useState(false);
  const [deleteDataError, setDeleteDataError] = useState(null);
  const [deleteDataSuccess, setDeleteDataSuccess] = useState(null);
  const [deleteDataConfirmOpen, setDeleteDataConfirmOpen] = useState(false);

  // Delete account state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Export schedule state
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [frequency, setFrequency] = useState('weekly');
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [timeOfDay, setTimeOfDay] = useState('09:00');
  const [exportEmail, setExportEmail] = useState('');
  const [exportingNow, setExportingNow] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [exportSuccess, setExportSuccess] = useState(null);

  useEffect(() => {
    loadSettings();
    loadExportSchedule();
    loadConnectedAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const [profileRes, currenciesRes] = await Promise.all([
        getUserProfile(),
        getSupportedCurrencies()
      ]);

      setProfile(profileRes.profile);
      setSelectedCurrency(profileRes.profile.default_currency_code);
      setCurrencies(currenciesRes.currencies);
    } catch (err) {
      setError(err.message);
      console.error('Error loading settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadExportSchedule = async () => {
    try {
      const res = await getExportSchedule();
      if (res.schedule) {
        setScheduleEnabled(res.schedule.enabled);
        setFrequency(res.schedule.frequency);
        setDayOfWeek(res.schedule.day_of_week || 1);
        setDayOfMonth(res.schedule.day_of_month || 1);
        setTimeOfDay(res.schedule.time_of_day || '09:00');
        setExportEmail(res.schedule.email);
      } else {
        // Pre-populate email from auth user
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.email) setExportEmail(user.email);
      }
    } catch (err) {
      console.error('Error loading export schedule:', err);
    }
  };

  const handleSaveSchedule = async () => {
    try {
      setSavingSchedule(true);
      setExportError(null);
      setExportSuccess(null);

      await saveExportSchedule({
        enabled: scheduleEnabled,
        frequency,
        day_of_week: frequency === 'weekly' ? dayOfWeek : null,
        day_of_month: frequency === 'monthly' ? dayOfMonth : null,
        time_of_day: timeOfDay,
        email: exportEmail
      });

      setExportSuccess('Export schedule saved!');
      setTimeout(() => setExportSuccess(null), 3000);
    } catch (err) {
      setExportError(err.message);
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleExportNow = async () => {
    try {
      setExportingNow(true);
      setExportError(null);
      setExportSuccess(null);

      const res = await exportNow(exportEmail);
      setExportSuccess(res.message || 'Export sent to your email!');
      setTimeout(() => setExportSuccess(null), 5000);
    } catch (err) {
      setExportError(err.message);
    } finally {
      setExportingNow(false);
    }
  };

  const loadConnectedAccounts = async () => {
    try {
      setAccountsLoading(true);
      const res = await getConnectedAccounts();
      setConnectedAccounts(res.accounts || []);
    } catch (err) {
      console.error('Error loading connected accounts:', err);
    } finally {
      setAccountsLoading(false);
    }
  };

  const handleLinkEmail = async () => {
    if (!linkEmailInput || !linkEmailInput.includes('@')) return;
    try {
      setLinkingEmail(true);
      setAccountsError(null);
      setAccountsSuccess(null);

      const result = await linkEmail(linkEmailInput);
      if (result.conflict) {
        setMergeState({ conflictEmail: result.conflictEmail, mergeToken: result.mergeToken });
      } else {
        setAccountsSuccess('Email linked successfully!');
        setLinkEmailInput('');
        loadConnectedAccounts();
        setTimeout(() => setAccountsSuccess(null), 3000);
      }
    } catch (err) {
      setAccountsError(err.message);
    } finally {
      setLinkingEmail(false);
    }
  };

  const handleMergeConfirm = async () => {
    if (!mergeState?.mergeToken) return;
    await mergeAccountsApi(mergeState.mergeToken);
    setMergeState(null);
    window.location.reload();
  };

  const handleConnectGoogle = async () => {
    try {
      await signInWithOAuth('google');
    } catch (err) {
      setAccountsError(err.message);
    }
  };

  const getProviderIcon = (provider) => {
    switch (provider) {
      case 'google': return '🔵';
      case 'telegram': return '✈️';
      case 'email': return '📧';
      case 'magic_link': return '🔗';
      default: return '🔑';
    }
  };

  const getProviderLabel = (provider) => {
    switch (provider) {
      case 'google': return 'Google';
      case 'telegram': return 'Telegram';
      case 'email': return 'Email';
      case 'magic_link': return 'Magic Link';
      default: return provider;
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      await updateUserProfile({
        default_currency_code: selectedCurrency
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message);
      console.error('Error saving settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteData = async () => {
    const selected = Object.entries(deleteDataCategories)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (selected.length === 0) return;
    try {
      setDeletingData(true);
      setDeleteDataError(null);
      setDeleteDataSuccess(null);
      const result = await deleteDataApi(selected);
      setDeleteDataSuccess(result.message);
      setDeleteDataCategories({ receipts: false, expenses: false, email_forwarding: false, telegram: false });
      setDeleteDataConfirmOpen(false);
      setTimeout(() => setDeleteDataSuccess(null), 5000);
    } catch (err) {
      setDeleteDataError(err.message);
    } finally {
      setDeletingData(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    try {
      setDeleting(true);
      setDeleteError(null);
      await deleteAccountApi();
      // Sign out and redirect
      await supabase.auth.signOut();
      window.location.href = '/';
    } catch (err) {
      setDeleteError(err.message);
      setDeleting(false);
    }
  };

  const styles = {
    container: {
      padding: 'var(--space-6)',
      maxWidth: '800px',
      margin: '0 auto',
    },
    title: {
      fontSize: 'var(--font-size-2xl)',
      fontWeight: 'var(--font-weight-semibold)',
      marginBottom: 'var(--space-6)',
      color: 'var(--color-text-primary)',
      letterSpacing: 'var(--letter-spacing-tight)',
    },
    section: {
      backgroundColor: 'var(--color-bg-base)',
      borderRadius: 'var(--radius-xl)',
      padding: 'var(--space-8)',
      marginBottom: 'var(--space-6)',
      boxShadow: 'var(--shadow-card)',
      border: '1px solid var(--color-border-light)',
    },
    sectionTitle: {
      fontSize: 'var(--font-size-lg)',
      fontWeight: 'var(--font-weight-semibold)',
      marginBottom: 'var(--space-4)',
      color: 'var(--color-text-primary)',
      letterSpacing: 'var(--letter-spacing-tight)',
    },
    label: {
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-medium)',
      color: 'var(--color-text-secondary)',
      display: 'block',
      marginBottom: 'var(--space-2)',
    },
    select: {
      width: '100%',
      padding: 'var(--space-2) var(--space-3)',
      borderRadius: 'var(--radius-default)',
      border: '1px solid var(--color-border-default)',
      fontSize: 'var(--font-size-sm)',
      backgroundColor: 'var(--color-bg-base)',
      color: 'var(--color-text-primary)',
      marginBottom: 'var(--space-4)',
      cursor: 'pointer',
      outline: 'none',
      transition: 'var(--transition-normal)',
    },
    infoBox: {
      padding: 'var(--space-3)',
      backgroundColor: 'var(--color-info-light)',
      border: '1px solid var(--color-border-default)',
      borderRadius: 'var(--radius-default)',
      marginBottom: 'var(--space-4)',
      fontSize: 'var(--font-size-sm)',
      color: 'var(--color-info)',
    },
    buttonGroup: {
      display: 'flex',
      gap: 'var(--space-3)',
      flexWrap: 'wrap',
    },
    saveButton: {
      padding: 'var(--space-2) var(--space-4)',
      borderRadius: 'var(--radius-lg)',
      border: 'none',
      backgroundColor: 'var(--color-primary)',
      color: 'var(--color-text-inverse)',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-medium)',
      cursor: 'pointer',
      transition: 'var(--transition-normal)',
      opacity: saving ? 0.6 : 1,
    },
    reloadButton: {
      padding: 'var(--space-2) var(--space-4)',
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--color-border-default)',
      backgroundColor: 'var(--color-bg-base)',
      color: 'var(--color-text-primary)',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-medium)',
      cursor: 'pointer',
      transition: 'var(--transition-normal)',
    },
    errorBox: {
      padding: 'var(--space-3)',
      backgroundColor: 'var(--color-error-light)',
      border: '1px solid var(--color-error)',
      borderRadius: 'var(--radius-default)',
      marginBottom: 'var(--space-4)',
      fontSize: 'var(--font-size-sm)',
      color: 'var(--color-error-dark)',
    },
    successBox: {
      padding: 'var(--space-3)',
      backgroundColor: 'var(--color-success-light)',
      border: '1px solid var(--color-success)',
      borderRadius: 'var(--radius-default)',
      marginBottom: 'var(--space-4)',
      fontSize: 'var(--font-size-sm)',
      color: 'var(--color-success)',
    },
    helpText: {
      fontSize: 'var(--font-size-xs)',
      color: 'var(--color-text-tertiary)',
      marginTop: 'var(--space-2)',
      fontStyle: 'italic',
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.title}>Settings</div>
        <div style={styles.section}>
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Settings</h2>

      {/* Currency Preferences Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>💱 Currency Preferences</h3>

        <div>
          <label style={styles.label}>Default Currency</label>
          <select
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
            style={styles.select}
            disabled={saving}
          >
            {currencies.map((currency) => (
              <option key={currency.code} value={currency.code}>
                {currency.code} - {currency.name} ({currency.symbol})
              </option>
            ))}
          </select>
          <p style={styles.helpText}>
            This currency will be used as your main currency for new expenses and reporting.
            Historical exchange rates are stored with each expense for accurate conversion.
          </p>
        </div>

        {error && (
          <div style={styles.errorBox}>
            ❌ {error}
          </div>
        )}

        {success && (
          <div style={styles.successBox}>
            ✅ Settings saved successfully!
          </div>
        )}

        <div style={styles.buttonGroup}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              ...styles.saveButton,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={(e) => {
              if (!saving) e.target.style.backgroundColor = 'var(--color-primary-hover)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--color-primary)';
            }}
          >
            {saving ? '💾 Saving...' : '💾 Save Settings'}
          </button>

          <button
            onClick={loadSettings}
            disabled={loading}
            style={{
              ...styles.reloadButton,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={(e) => {
              if (!loading) e.target.style.backgroundColor = 'var(--color-bg-muted)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--color-bg-base)';
            }}
          >
            🔄 Reload
          </button>
        </div>
      </div>

      {/* Connected Accounts Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>🔗 Connected Accounts</h3>

        {accountsLoading ? (
          <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>Loading accounts...</p>
        ) : (
          <>
            {connectedAccounts.length > 0 && (
              <div style={{ marginBottom: 'var(--space-4)' }}>
                {connectedAccounts.map((account, idx) => (
                  <div key={`${account.provider}-${idx}`} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-3)',
                    backgroundColor: 'var(--color-bg-subtle, #f9fafb)',
                    borderRadius: 'var(--radius-default)',
                    marginBottom: 'var(--space-2)',
                    border: '1px solid var(--color-border-light)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                      <span style={{ fontSize: '20px' }}>{getProviderIcon(account.provider)}</span>
                      <div>
                        <div style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
                          {getProviderLabel(account.provider)}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                          {account.provider_email || account.provider_metadata?.username || account.provider_id}
                        </div>
                      </div>
                    </div>
                    <span style={{
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--color-success, #10b981)',
                      backgroundColor: 'var(--color-success-light, #d1fae5)',
                      padding: '2px 8px',
                      borderRadius: '12px',
                    }}>
                      Connected
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Connect Google button (if not linked) */}
            {!connectedAccounts.some(a => a.provider === 'google') && (
              <button
                onClick={handleConnectGoogle}
                style={{
                  ...styles.reloadButton,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  marginBottom: 'var(--space-3)',
                  width: '100%',
                  justifyContent: 'center',
                }}
              >
                🔵 Connect Google Account
              </button>
            )}

            {/* Add email (for telegram-only users) */}
            {isTelegramOnlyUser && (
              <div style={{ marginTop: 'var(--space-3)' }}>
                <label style={styles.label}>Add Email Address</label>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <input
                    type="email"
                    value={linkEmailInput}
                    onChange={(e) => setLinkEmailInput(e.target.value)}
                    placeholder="you@example.com"
                    style={{ ...styles.select, flex: 1, marginBottom: 0 }}
                    disabled={linkingEmail}
                  />
                  <button
                    onClick={handleLinkEmail}
                    disabled={linkingEmail || !linkEmailInput}
                    style={{
                      ...styles.saveButton,
                      opacity: (linkingEmail || !linkEmailInput) ? 0.6 : 1,
                      cursor: (linkingEmail || !linkEmailInput) ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {linkingEmail ? 'Linking...' : 'Link Email'}
                  </button>
                </div>
                <p style={styles.helpText}>
                  Adding an email lets you sign in with email/password and recover your account.
                </p>
              </div>
            )}

            {accountsError && (
              <div style={styles.errorBox}>❌ {accountsError}</div>
            )}
            {accountsSuccess && (
              <div style={styles.successBox}>✅ {accountsSuccess}</div>
            )}
          </>
        )}
      </div>

      {mergeState && (
        <MergeConfirmModal
          conflictEmail={mergeState.conflictEmail}
          onConfirm={handleMergeConfirm}
          onCancel={() => setMergeState(null)}
        />
      )}

      {/* Scheduled CSV Export Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>📧 Scheduled CSV Export</h3>

        <div style={{ marginBottom: 'var(--space-4)' }}>
          <label style={{ ...styles.label, display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={scheduleEnabled}
              onChange={(e) => setScheduleEnabled(e.target.checked)}
              style={{ marginRight: 'var(--space-2)' }}
            />
            Enable scheduled email exports
          </label>
        </div>

        {scheduleEnabled && (
          <>
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={styles.label}>Frequency</label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                style={styles.select}
                disabled={savingSchedule}
              >
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            {frequency === 'weekly' ? (
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label style={styles.label}>Day of Week</label>
                <select
                  value={dayOfWeek}
                  onChange={(e) => setDayOfWeek(parseInt(e.target.value))}
                  style={styles.select}
                  disabled={savingSchedule}
                >
                  <option value={1}>Monday</option>
                  <option value={2}>Tuesday</option>
                  <option value={3}>Wednesday</option>
                  <option value={4}>Thursday</option>
                  <option value={5}>Friday</option>
                  <option value={6}>Saturday</option>
                  <option value={7}>Sunday</option>
                </select>
              </div>
            ) : (
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label style={styles.label}>Day of Month (1-28)</label>
                <select
                  value={dayOfMonth}
                  onChange={(e) => setDayOfMonth(parseInt(e.target.value))}
                  style={styles.select}
                  disabled={savingSchedule}
                >
                  {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={styles.label}>Time</label>
              <input
                type="time"
                value={timeOfDay}
                onChange={(e) => setTimeOfDay(e.target.value)}
                style={{ ...styles.select, cursor: 'text' }}
                disabled={savingSchedule}
              />
            </div>
          </>
        )}

        <div style={{ marginBottom: 'var(--space-4)' }}>
          <label style={styles.label}>Email Address</label>
          <input
            type="email"
            value={exportEmail}
            onChange={(e) => setExportEmail(e.target.value)}
            placeholder="your@email.com"
            style={{ ...styles.select, cursor: 'text' }}
            disabled={savingSchedule || exportingNow}
          />
          <p style={styles.helpText}>
            CSV exports will be sent to this email address.
          </p>
        </div>

        {scheduleEnabled && (
          <div style={{ ...styles.infoBox, marginBottom: 'var(--space-4)' }}>
            Automatic exports will be sent {frequency === 'weekly'
              ? `every ${['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][dayOfWeek - 1]}`
              : `on day ${dayOfMonth} of each month`
            } at {timeOfDay}.
          </div>
        )}

        {exportError && (
          <div style={styles.errorBox}>
            ❌ {exportError}
          </div>
        )}

        {exportSuccess && (
          <div style={styles.successBox}>
            ✅ {exportSuccess}
          </div>
        )}

        <div style={styles.buttonGroup}>
          {scheduleEnabled && (
            <button
              onClick={handleSaveSchedule}
              disabled={savingSchedule || !exportEmail}
              style={{
                ...styles.saveButton,
                cursor: (savingSchedule || !exportEmail) ? 'not-allowed' : 'pointer',
                opacity: (savingSchedule || !exportEmail) ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (!savingSchedule && exportEmail) e.target.style.backgroundColor = 'var(--color-primary-hover)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'var(--color-primary)';
              }}
            >
              {savingSchedule ? '💾 Saving...' : '💾 Save Schedule'}
            </button>
          )}

          <button
            onClick={handleExportNow}
            disabled={exportingNow || !exportEmail}
            style={{
              ...styles.reloadButton,
              cursor: (exportingNow || !exportEmail) ? 'not-allowed' : 'pointer',
              opacity: (exportingNow || !exportEmail) ? 0.6 : 1,
            }}
            onMouseEnter={(e) => {
              if (!exportingNow && exportEmail) e.target.style.backgroundColor = 'var(--color-bg-muted)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--color-bg-base)';
            }}
          >
            {exportingNow ? '📤 Sending...' : '📤 Export to Email Now'}
          </button>
        </div>
      </div>

      {/* Info Section */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>ℹ️ About Multi-Currency</h3>
        <div style={{ fontSize: 'var(--font-size-sm)', lineHeight: '1.5', color: 'var(--color-text-secondary)' }}>
          <p>
            Your expense tracker supports over 160 currencies. When you create an expense in a different currency,
            the exchange rate at that time is stored with the expense for accurate historical tracking.
          </p>
          <p>
            Exchange rates are updated within the last 24 hours. Your totals and
            averages are automatically calculated in your default currency.
          </p>
        </div>
      </div>

      {/* Delete Data Section */}
      <div style={styles.section}>
        <h3 style={{ ...styles.sectionTitle, color: '#d97706' }}>Delete Data</h3>
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)', lineHeight: '1.5' }}>
          Selectively delete specific data from your account. Your account and profile will remain active.
        </p>

        <div style={{ marginBottom: 'var(--space-4)' }}>
          {[
            { key: 'receipts', label: 'Receipt images', desc: 'Uploaded receipt photos and OCR text (expense records are kept)' },
            { key: 'expenses', label: 'Expenses data', desc: 'All expense records, including receipt images and bank statement entries' },
            { key: 'email_forwarding', label: 'Email forwarded records', desc: 'Forwarding tokens and forwarded email history' },
            { key: 'telegram', label: 'Telegram chat records', desc: 'Telegram bot conversation history' },
          ].map(({ key, label, desc }) => (
            <label key={key} style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-3)',
              padding: 'var(--space-3)',
              borderRadius: 'var(--radius-default)',
              marginBottom: 'var(--space-2)',
              cursor: 'pointer',
              backgroundColor: deleteDataCategories[key] ? '#fffbeb' : 'transparent',
              border: `1px solid ${deleteDataCategories[key] ? '#fcd34d' : 'var(--color-border-light)'}`,
              transition: 'var(--transition-normal)',
            }}>
              <input
                type="checkbox"
                checked={deleteDataCategories[key]}
                onChange={(e) => setDeleteDataCategories(prev => ({ ...prev, [key]: e.target.checked }))}
                style={{ marginTop: '2px' }}
                disabled={deletingData}
              />
              <div>
                <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>
                  {label}
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                  {desc}
                </div>
              </div>
            </label>
          ))}
        </div>

        {deleteDataError && (
          <div style={{ ...styles.errorBox, marginBottom: 'var(--space-3)' }}>
            {deleteDataError}
          </div>
        )}

        {deleteDataSuccess && (
          <div style={{ ...styles.successBox, marginBottom: 'var(--space-3)' }}>
            {deleteDataSuccess}
          </div>
        )}

        {!deleteDataConfirmOpen ? (
          <button
            onClick={() => setDeleteDataConfirmOpen(true)}
            disabled={!Object.values(deleteDataCategories).some(v => v)}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid #d97706',
              backgroundColor: 'transparent',
              color: '#d97706',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: !Object.values(deleteDataCategories).some(v => v) ? 'not-allowed' : 'pointer',
              opacity: !Object.values(deleteDataCategories).some(v => v) ? 0.5 : 1,
              transition: 'var(--transition-normal)',
            }}
            onMouseEnter={(e) => {
              if (Object.values(deleteDataCategories).some(v => v)) {
                e.target.style.backgroundColor = '#d97706'; e.target.style.color = '#fff';
              }
            }}
            onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = '#d97706'; }}
          >
            Delete Selected Data
          </button>
        ) : (
          <div style={{
            padding: 'var(--space-4)',
            backgroundColor: '#fffbeb',
            borderRadius: 'var(--radius-default)',
            border: '1px solid #fcd34d',
          }}>
            <p style={{ fontSize: 'var(--font-size-sm)', color: '#92400e', fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--space-3)' }}>
              Are you sure you want to delete the selected data? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
              <button
                onClick={handleDeleteData}
                disabled={deletingData}
                style={{
                  padding: 'var(--space-2) var(--space-4)',
                  borderRadius: 'var(--radius-lg)',
                  border: 'none',
                  backgroundColor: '#d97706',
                  color: '#fff',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  cursor: deletingData ? 'not-allowed' : 'pointer',
                  opacity: deletingData ? 0.6 : 1,
                }}
              >
                {deletingData ? 'Deleting...' : 'Confirm Delete'}
              </button>
              <button
                onClick={() => { setDeleteDataConfirmOpen(false); setDeleteDataError(null); }}
                disabled={deletingData}
                style={{
                  ...styles.reloadButton,
                  cursor: deletingData ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Account Section */}
      <div style={{
        ...styles.section,
        border: '1px solid #fecaca',
        backgroundColor: 'var(--color-bg-base)',
      }}>
        <h3 style={{ ...styles.sectionTitle, color: '#dc2626' }}>Delete Account</h3>
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)', lineHeight: '1.5' }}>
          Permanently delete your account and all associated data. This includes all expenses, receipts, connected accounts, and activity history. This action cannot be undone.
        </p>

        {!deleteConfirmOpen ? (
          <button
            onClick={() => setDeleteConfirmOpen(true)}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid #dc2626',
              backgroundColor: 'transparent',
              color: '#dc2626',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
              transition: 'var(--transition-normal)',
            }}
            onMouseEnter={(e) => { e.target.style.backgroundColor = '#dc2626'; e.target.style.color = '#fff'; }}
            onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = '#dc2626'; }}
          >
            Delete My Account
          </button>
        ) : (
          <div style={{
            padding: 'var(--space-4)',
            backgroundColor: '#fef2f2',
            borderRadius: 'var(--radius-default)',
            border: '1px solid #fecaca',
          }}>
            <p style={{ fontSize: 'var(--font-size-sm)', color: '#991b1b', fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--space-3)' }}>
              Are you sure? Type <strong>DELETE</strong> to confirm.
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE to confirm"
              style={{
                ...styles.select,
                marginBottom: 'var(--space-3)',
                borderColor: '#fecaca',
              }}
              disabled={deleting}
              autoFocus
            />

            {deleteError && (
              <div style={{ ...styles.errorBox, marginBottom: 'var(--space-3)' }}>
                {deleteError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || deleting}
                style={{
                  padding: 'var(--space-2) var(--space-4)',
                  borderRadius: 'var(--radius-lg)',
                  border: 'none',
                  backgroundColor: deleteConfirmText === 'DELETE' ? '#dc2626' : '#9ca3af',
                  color: '#fff',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  cursor: (deleteConfirmText !== 'DELETE' || deleting) ? 'not-allowed' : 'pointer',
                  opacity: deleting ? 0.6 : 1,
                }}
              >
                {deleting ? 'Deleting...' : 'Permanently Delete Account'}
              </button>
              <button
                onClick={() => { setDeleteConfirmOpen(false); setDeleteConfirmText(''); setDeleteError(null); }}
                disabled={deleting}
                style={{
                  ...styles.reloadButton,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SettingsPage;
